import API_BASE from '../config';
import React, { useEffect, useRef, useState } from 'react';

export type PhotonSuggestion = {
  street: string;
  city: string;
  region: string;
  zip: string;
  country: string;
  label: string;
  name?: string;
};

const DISMISS_EVENT = 'photonSuggestDismiss';

const defaultLabelCls = 'block text-xs font-medium text-gray-700 mb-1.5';
const defaultInputCls =
  'w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-50';

type Props = {
  id?: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  onSelect: (suggestion: PhotonSuggestion) => void;
  placeholder?: string;
  disabled?: boolean;
  labelClassName?: string;
  inputClassName?: string;
  /** Hide built-in label (e.g. when wrapped in FormRow) */
  hideLabel?: boolean;
};

const PhotonSuggestInput: React.FC<Props> = ({
  id,
  label,
  value,
  onChange,
  onSelect,
  placeholder,
  disabled,
  labelClassName = defaultLabelCls,
  inputClassName = defaultInputCls,
  hideLabel = false,
}) => {
  const [suggestions, setSuggestions] = useState<PhotonSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const allowSearch = useRef(false);

  const dismiss = () => {
    setSuggestions([]);
    setOpen(false);
    setLoading(false);
  };

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onDismissAll = () => {
      allowSearch.current = false;
      dismiss();
    };
    document.addEventListener('mousedown', onDocClick);
    window.addEventListener(DISMISS_EVENT, onDismissAll);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      window.removeEventListener(DISMISS_EVENT, onDismissAll);
    };
  }, []);

  useEffect(() => {
    if (!allowSearch.current) {
      dismiss();
      return;
    }
    const q = value.trim();
    if (q.length < 3) {
      dismiss();
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE}/api/photon/suggest?q=${encodeURIComponent(q)}`, {
          credentials: 'include',
        });
        const data = await res.json();
        if (cancelled) return;
        if (!res.ok) {
          dismiss();
          return;
        }
        const list = data.suggestions || [];
        setSuggestions(list);
        setOpen(list.length > 0);
      } catch {
        if (!cancelled) dismiss();
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 320);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [value]);

  const handleSelect = (s: PhotonSuggestion) => {
    allowSearch.current = false;
    dismiss();
    window.dispatchEvent(new Event(DISMISS_EVENT));
    onSelect(s);
  };

  return (
    <div ref={wrapRef} className="relative w-full">
      {!hideLabel && (
        <label htmlFor={id} className={labelClassName}>
          {label}
        </label>
      )}
      <input
        id={id}
        type="text"
        value={value}
        disabled={disabled}
        autoComplete="off"
        onChange={e => {
          allowSearch.current = true;
          onChange(e.target.value);
        }}
        onFocus={() => {
          if (suggestions.length > 0) setOpen(true);
        }}
        placeholder={placeholder}
        className={inputClassName}
        aria-label={hideLabel ? label : undefined}
      />
      {loading && (
        <p
          className={`absolute right-3 text-[11px] text-gray-400 ${
            hideLabel ? 'top-2' : 'top-[2.15rem]'
          }`}
        >
          …
        </p>
      )}
      {open && suggestions.length > 0 && (
        <ul className="absolute z-30 mt-1 w-full max-h-56 overflow-auto rounded-[6px] border border-gray-200 bg-white shadow-lg">
          {suggestions.map((s, i) => (
            <li key={`${s.label}-${i}`}>
              <button
                type="button"
                onMouseDown={e => e.preventDefault()}
                onClick={() => handleSelect(s)}
                className="w-full text-left px-2.5 py-2 hover:bg-primary-50 transition-colors border-b border-gray-50 last:border-0"
              >
                <div className="text-[13px] font-medium text-gray-900 truncate">{s.street || s.name || s.label}</div>
                <div className="text-[11px] text-gray-500 mt-0.5 truncate">{s.label}</div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default PhotonSuggestInput;
